import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, Menu, X, Crown, Coins, MessageCircle, LogOut, Settings, LayoutDashboard, UserCircle, Compass, Users, ShieldCheck, HelpCircle, FileText } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import NotificationBell from "@/components/notifications/NotificationBell";

const MobileTopHeader = () => {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const path = location.pathname;

  // Hide on admin pages, chat pages, login/signup, onboarding, and homepage for guests
  if (
    path.startsWith("/admin") ||
    path.startsWith("/chat/") ||
    path === "/login" ||
    path === "/signup" ||
    path === "/forgot-password" ||
    path === "/onboarding" ||
    (path === "/" && !user)
  ) return null;

  return (
    <>
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border/50">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
            <Heart className="h-6 w-6 text-primary fill-primary" />
            <span className="font-display text-lg font-bold text-foreground">
              Lovable<span className="text-primary">Pinay</span>
            </span>
          </Link>

          <div className="flex items-center gap-1.5">
            {user && <NotificationBell />}
            <button
              onClick={() => setOpen(!open)}
              className="p-2 text-foreground"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden fixed top-14 left-0 right-0 z-50 bg-card border-b border-border overflow-hidden"
          >
            <div className="px-4 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
              <NavItem to="/discover" icon={Compass} label="Discover" onClick={() => setOpen(false)} active={path === "/discover"} />
              {user && (
                <>
                  <NavItem to="/matches" icon={Users} label="Matches" onClick={() => setOpen(false)} active={path === "/matches"} />
                  <NavItem to="/messages" icon={MessageCircle} label="Messages" onClick={() => setOpen(false)} active={path === "/messages"} />
                  <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" onClick={() => setOpen(false)} active={path === "/dashboard"} />
                  <NavItem to="/profile/edit" icon={UserCircle} label="Edit Profile" onClick={() => setOpen(false)} active={path === "/profile/edit"} />
                  <NavItem to="/settings" icon={Settings} label="Settings" onClick={() => setOpen(false)} active={path === "/settings"} />
                </>
              )}

              <Separator className="my-2" />

              <NavItem to="/vip" icon={Crown} label="VIP Membership" onClick={() => setOpen(false)} active={path === "/vip"} highlight="amber" />
              <NavItem to="/credits" icon={Coins} label="Buy Credits" onClick={() => setOpen(false)} active={path === "/credits"} highlight="primary" />
              <NavItem to="/pricing" icon={FileText} label="Compare Plans" onClick={() => setOpen(false)} active={path === "/pricing"} />

              <Separator className="my-2" />

              <NavItem to="/safety" icon={ShieldCheck} label="Safety" onClick={() => setOpen(false)} active={path === "/safety"} />
              <NavItem to="/help" icon={HelpCircle} label="Help & FAQ" onClick={() => setOpen(false)} active={path === "/help"} />

              <div className="pt-3">
                {user ? (
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 text-muted-foreground hover:text-destructive"
                    onClick={() => { signOut(); setOpen(false); }}
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </Button>
                ) : (
                  <div className="flex gap-3">
                    <Button variant="ghost" size="sm" asChild className="flex-1" onClick={() => setOpen(false)}>
                      <Link to="/login">Log In</Link>
                    </Button>
                    <Button variant="hero" size="sm" asChild className="flex-1" onClick={() => setOpen(false)}>
                      <Link to="/signup">Sign Up Free</Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-40 bg-foreground/20 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const NavItem = ({
  to, icon: Icon, label, onClick, active, highlight,
}: {
  to: string;
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  active: boolean;
  highlight?: "amber" | "primary";
}) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-colors active:scale-[0.98] ${
      active
        ? "bg-primary/10 text-primary"
        : highlight === "amber"
        ? "text-amber-600 hover:bg-amber-500/10"
        : highlight === "primary"
        ? "text-primary hover:bg-primary/10"
        : "text-muted-foreground hover:bg-muted"
    }`}
  >
    <Icon className="h-5 w-5" />
    {label}
  </Link>
);

export default MobileTopHeader;
